import { IconHome, IconPlus } from "@tabler/icons-react";
import { Link } from "react-router";
import { ErrorState, LoadingState } from "@how/ui";
import { useBrand } from "../brand-queries";
import { useTenantRecord } from "../../tenant/queries";
import { PAGE_STATUS_LABEL, pagePath, pageStatus, type PageRow } from "../website-queries";
import { websiteHref } from "../website-variant";
import { SiteThumb } from "./SiteThumb";

const RELATIVE = new Intl.RelativeTimeFormat("en-US", { numeric: "auto" });

/** "today", "yesterday", "3 days ago", "2 weeks ago", "4 months ago". */
export function since(iso: string, now = Date.now()): string {
  const days = Math.round((Date.parse(iso) - now) / 86_400_000);
  if (Math.abs(days) < 7) return RELATIVE.format(days, "day");
  if (Math.abs(days) < 60) return RELATIVE.format(Math.round(days / 7), "week");
  if (Math.abs(days) < 365) return RELATIVE.format(Math.round(days / 30), "month");
  return RELATIVE.format(Math.round(days / 365), "year");
}

/** The home page first, as it is the one every visitor lands on; the rest as they came (by title). */
function ordered(pages: readonly PageRow[]): PageRow[] {
  return [...pages].sort((a, b) => Number(b.slug === "home") - Number(a.slug === "home"));
}

/**
 * The pages as cards (studio design): each a miniature of the page in the church's own colours, its title and address,
 * whether it is live, and when it was last published; a card to add one at the end.
 */
export function PageCards({
  pages,
  isError,
  onRetry,
  onAdd,
}: {
  readonly pages: readonly PageRow[] | undefined;
  readonly isError: boolean;
  readonly onRetry: () => void;
  readonly onAdd: (() => void) | null;
}) {
  const brand = useBrand();
  const tenant = useTenantRecord();
  if (isError) return <ErrorState message="Could not load pages." onRetry={onRetry} />;
  if (pages === undefined) return <LoadingState rows={3} label="Loading your pages" />;
  const counts = { published: 0, changed: 0, draft: 0 };
  for (const p of pages) counts[pageStatus(p)] += 1;
  const churchName = tenant.data?.name ?? "Your church";
  return (
    <>
      <ul className="ws-stats" aria-label="Pages at a glance">
        <li>
          <strong>{pages.length}</strong> {pages.length === 1 ? "page" : "pages"}
        </li>
        <li data-state="published">
          <span className="ws-dot" aria-hidden />
          <strong>{counts.published}</strong> published
        </li>
        <li data-state="changed">
          <span className="ws-dot" aria-hidden />
          <strong>{counts.changed}</strong> with unpublished changes
        </li>
        <li data-state="draft">
          <span className="ws-dot" aria-hidden />
          <strong>{counts.draft}</strong> not published
        </li>
      </ul>
      <ul className="ws-cards" aria-label="Pages">
        {ordered(pages).map((page) => {
          const status = pageStatus(page);
          const address = pagePath(page.published_slug ?? page.slug);
          const home = page.slug === "home";
          return (
            <li key={page.id}>
              <Link to={websiteHref("studio", `/website/pages/${page.id}`)} className="ws-card">
                <SiteThumb
                  brand={brand.data?.brand}
                  title={page.title}
                  address={address}
                  churchName={churchName}
                  variant={home ? "home" : "page"}
                />
                <span className="ws-card__body">
                  <span className="ws-card__title-line">
                    {home ? <IconHome size={16} stroke={2} aria-label="Home page" /> : null}
                    <span className="ws-card__title">{page.title}</span>
                  </span>
                  <span className="ws-card__address">{address}</span>
                  <span className="ws-card__foot">
                    <span className="ws-card__status" data-state={status}>
                      <span className="ws-dot" aria-hidden />
                      {PAGE_STATUS_LABEL[status]}
                    </span>
                    <span className="ws-card__when">
                      {page.published_at === null
                        ? "Never published"
                        : `Published ${since(page.published_at)}`}
                    </span>
                  </span>
                </span>
              </Link>
            </li>
          );
        })}
        {onAdd === null ? null : (
          <li>
            <button type="button" className="ws-card ws-card--add" onClick={onAdd}>
              <span className="ws-card--add__icon" aria-hidden>
                <IconPlus size={22} />
              </span>
              Add a page
            </button>
          </li>
        )}
      </ul>
      {pages.length === 0 ? (
        <p className="ws-note">Add a home page to start your church&apos;s website.</p>
      ) : null}
    </>
  );
}
